/** Cliente da API do Hub para a PWA. Access token só em memória; o refresh fica no cookie HttpOnly (D-20261007-07). */

export class ErroApi extends Error {
  constructor(readonly status: number, readonly codigo: string, mensagem: string) {
    super(mensagem);
  }
}

let token: string | undefined;
let venceEm = 0;
let aoExpirar: () => void = () => undefined;

export const definirAoExpirar = (f: () => void) => {
  aoExpirar = f;
};

export const temToken = () => !!token;

const bruto = async <T>(metodo: string, caminho: string, corpo?: unknown, autenticado = true): Promise<T> => {
  const r = await fetch(`/api/${caminho}`, {
    method: metodo,
    credentials: "same-origin",
    headers: {
      "x-escalando": "1",
      ...(corpo !== undefined ? { "content-type": "application/json" } : {}),
      ...(autenticado && token ? { authorization: `Bearer ${token}` } : {}),
    },
    ...(corpo !== undefined ? { body: JSON.stringify(corpo) } : {}),
  });
  const dados = (await r.json().catch(() => ({}))) as T & { erro?: string; mensagem?: string; message?: string };
  if (!r.ok) throw new ErroApi(r.status, dados.erro ?? "erro", dados.mensagem ?? dados.message ?? `erro ${r.status}`);
  return dados;
};

export type EtapaLogin =
  | { etapa: "ok"; access_token: string; expira_em: number }
  | { etapa: "nova_senha" | "mfa" | "configurar_mfa"; sessao: string }
  | { etapa: "confirmar_mfa"; sessao: string; segredo: string; otpauth: string };

const guardar = (e: EtapaLogin) => {
  if (e.etapa === "ok") {
    token = e.access_token;
    venceEm = Date.now() + (e.expira_em - 60) * 1000;
  }
  return e;
};

export const auth = {
  entrar: (email: string, senha: string) => bruto<EtapaLogin>("POST", "auth/entrar", { email, senha }, false).then(guardar),
  novaSenha: (email: string, sessao: string, senha: string) => bruto<EtapaLogin>("POST", "auth/nova-senha", { email, sessao, senha }, false).then(guardar),
  configurarMfa: (email: string, sessao: string) => bruto<EtapaLogin>("POST", "auth/configurar-mfa", { email, sessao }, false),
  confirmarMfa: (email: string, sessao: string, codigo: string) => bruto<EtapaLogin>("POST", "auth/confirmar-mfa", { email, sessao, codigo }, false).then(guardar),
  mfa: (email: string, sessao: string, codigo: string) => bruto<EtapaLogin>("POST", "auth/mfa", { email, sessao, codigo }, false).then(guardar),
  /** Usa o cookie da sessão (até 24 h desde o login com MFA). */
  renovar: () => bruto<EtapaLogin>("POST", "auth/renovar", {}, false).then(guardar),
  sair: async () => {
    token = undefined;
    await bruto("POST", "auth/sair", {}, false).catch(() => undefined);
  },
};

/** Chamada autenticada a /api/v1; renova o token quando preciso; sessão vencida leva de volta ao login. */
export const api = async <T>(metodo: "GET" | "POST", caminho: string, corpo?: unknown): Promise<T> => {
  if (!token || Date.now() > venceEm) await auth.renovar().catch(() => undefined);
  try {
    return await bruto<T>(metodo, `v1/${caminho}`, corpo);
  } catch (e) {
    if (e instanceof ErroApi && e.status === 403 && /sessão expirada|entre de novo/.test(e.message)) {
      token = undefined;
      aoExpirar();
    }
    throw e;
  }
};
